// src/admin/pages/blogs/Blogcommentmanagement.tsx
import React, { useState, useEffect } from 'react';
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  Drawer,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Close as CloseIcon,
  Delete as DeleteIcon,
  Comment as CommentIcon,
  ChatBubbleOutline as EmptyCommentIcon,
} from '@mui/icons-material';
import {
  collection,
  getDocs,
  doc,
  deleteDoc,
  orderBy,
  query,
} from 'firebase/firestore';
import { db } from '../../../firebase/config';
import { Blog } from './blog';
import { useAuth } from '../../../contexts/AuthContext';
import ToastNotification from '../common/Toastnotification';

/* ===================== TYPES ===================== */

interface Comment {
  id: string;
  name: string;
  email: string;
  content: string;
  createdAt: any;
}

interface ToastState {
  open: boolean;
  message: string;
  severity: 'success' | 'error' | 'warning' | 'info';
}

/* ===================== PROPS ===================== */

interface BlogCommentManagementProps {
  open: boolean;
  blog: Blog | null;
  onClose: () => void;
}

/* ===================== HELPERS ===================== */

const toDateSafe = (timestamp: any): Date | null => {
  if (!timestamp) return null;
  if (typeof timestamp.toDate === 'function') return timestamp.toDate();
  if (timestamp.seconds) return new Date(timestamp.seconds * 1000);
  if (typeof timestamp === 'string' || typeof timestamp === 'number') {
    const d = new Date(timestamp);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

const formatCommentDate = (timestamp: any): string => {
  const date = toDateSafe(timestamp);
  if (!date) return '—';
  return (
    date.toLocaleDateString('en-US', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }) +
    ' · ' +
    date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
  );
};

const getAvatarColor = (name: string): string => {
  const colors = [
    '#717fe0',
    '#e07171',
    '#71c9e0',
    '#e0a871',
    '#71e09a',
    '#c471e0',
  ];
  let hash = 0;
  for (let i = 0; i < name.length; i++)
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return colors[Math.abs(hash) % colors.length];
};

/* ===================== COMPONENT ===================== */

const BlogCommentManagement: React.FC<BlogCommentManagementProps> = ({
  open,
  blog,
  onClose,
}) => {
  const { hasPermission, isSuperAdmin } = useAuth();

  // ── Permission flags ───────────────────────────────────────────────────────
  // The parent (BlogManagement) already guards whether this drawer is opened
  // at all. These flags additionally control the delete action inside the drawer,
  // allowing the component to be safely composed in any future context too.
  const canManageComments = isSuperAdmin || hasPermission('blog.comments.manage');

  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState<string>('');

  const [toast, setToast] = useState<ToastState>({
    open: false,
    message: '',
    severity: 'info',
  });

  // Delete confirmation dialog state
  const [deleteTarget, setDeleteTarget] = useState<Comment | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  /* ── Fetch comments whenever the drawer opens for a blog ── */
  useEffect(() => {
    if (open && blog?.id) {
      fetchComments(blog.id);
    } else {
      setComments([]);
    }
  }, [open, blog?.id]);

  const fetchComments = async (blogId: string) => {
    try {
      setLoading(true);
      const commentsRef = collection(db, 'blogs', blogId, 'comments');
      const q = query(commentsRef, orderBy('createdAt', 'asc'));
      const snapshot = await getDocs(q);
      const data = snapshot.docs.map((d) => ({
        id: d.id,
        ...d.data(),
      })) as Comment[];
      setComments(data);
    } catch (err) {
      console.error('Error fetching comments:', err);
      showToast('Failed to load comments', 'error');
    } finally {
      setLoading(false);
    }
  };

  /* ── Toast helpers ── */
  const showToast = (message: string, severity: ToastState['severity']) => {
    setToast({ open: true, message, severity });
  };

  /* ── Delete ── */
  const handleDeleteClick = (comment: Comment) => {
    // Extra safety guard inside the component — redundant if parent already
    // gates access, but protects against accidental direct renders.
    if (!canManageComments) return;
    setDeleteTarget(comment);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget || !blog?.id) return;
    try {
      setDeleteLoading(true);
      setActionLoading(deleteTarget.id);
      await deleteDoc(
        doc(db, 'blogs', blog.id, 'comments', deleteTarget.id)
      );
      setComments((prev) => prev.filter((c) => c.id !== deleteTarget.id));
      showToast('Comment deleted successfully', 'success');
      setDeleteTarget(null);
    } catch (err) {
      console.error('Error deleting comment:', err);
      showToast('Failed to delete comment', 'error');
    } finally {
      setDeleteLoading(false);
      setActionLoading('');
    }
  };

  const handleDeleteCancel = () => {
    setDeleteTarget(null);
  };

  /* ===================== RENDER ===================== */

  return (
    <>
      <Drawer
        anchor="right"
        open={open}
        onClose={onClose}
        PaperProps={{ sx: { width: { xs: '100%', sm: 640 } } }}
      >
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
          {/* ── Header ── */}
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: 1,
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              flexShrink: 0,
              bgcolor: 'background.paper',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1.5}>
              <CommentIcon color="primary" />
              <Box>
                <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
                  Comments
                </Typography>
                {blog && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{
                      display: 'block',
                      maxWidth: 380,
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {blog.title}
                  </Typography>
                )}
              </Box>
            </Stack>
            <IconButton onClick={onClose} edge="end">
              <CloseIcon />
            </IconButton>
          </Box>

          {/* ── Stats Strip ── */}
          {!loading && comments.length > 0 && (
            <Box
              sx={{
                px: 3,
                py: 1.5,
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'action.hover',
                flexShrink: 0,
              }}
            >
              <Chip
                label={`${comments.length} Comment${comments.length !== 1 ? 's' : ''}`}
                size="small"
                variant="outlined"
                sx={{ fontWeight: 600, fontSize: '0.75rem' }}
              />
            </Box>
          )}

          {/* ── Body ── */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              minHeight: 0,
              '&::-webkit-scrollbar': { width: '6px' },
              '&::-webkit-scrollbar-track': { background: 'transparent' },
              '&::-webkit-scrollbar-thumb': {
                background: 'rgba(0,0,0,0.2)',
                borderRadius: '3px',
              },
            }}
          >
            {loading ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  flexDirection: 'column',
                  gap: 2,
                }}
              >
                <CircularProgress size={36} />
                <Typography variant="body2" color="text.secondary">
                  Loading comments...
                </Typography>
              </Box>
            ) : comments.length === 0 ? (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flex: 1,
                  flexDirection: 'column',
                  gap: 2,
                  p: 4,
                }}
              >
                <EmptyCommentIcon
                  sx={{ fontSize: 64, color: 'text.disabled', opacity: 0.4 }}
                />
                <Typography variant="h6" color="text.secondary">
                  No Comments Yet
                </Typography>
                <Typography
                  variant="body2"
                  color="text.disabled"
                  textAlign="center"
                >
                  This blog post hasn't received any comments yet.
                </Typography>
              </Box>
            ) : (
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{ borderRadius: 0 }}
              >
                <Table size="medium" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>
                        Commenter
                      </TableCell>
                      <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>
                        Comment
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          bgcolor: 'background.paper',
                          display: { xs: 'none', sm: 'table-cell' },
                        }}
                      >
                        Date
                      </TableCell>
                      {/* Actions column header — only shown when user can manage comments */}
                      {canManageComments && (
                        <TableCell
                          align="right"
                          sx={{ fontWeight: 700, bgcolor: 'background.paper' }}
                        >
                          Actions
                        </TableCell>
                      )}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {comments.map((comment) => (
                      <TableRow
                        key={comment.id}
                        sx={{ '&:last-child td': { borderBottom: 0 } }}
                      >
                        {/* Commenter */}
                        <TableCell sx={{ minWidth: 140, maxWidth: 160 }}>
                          <Stack direction="row" spacing={1.5} alignItems="center">
                            <Avatar
                              sx={{
                                width: 36,
                                height: 36,
                                fontSize: '0.875rem',
                                fontWeight: 700,
                                bgcolor: getAvatarColor(comment.name),
                                flexShrink: 0,
                              }}
                            >
                              {comment.name.charAt(0).toUpperCase()}
                            </Avatar>
                            <Box minWidth={0}>
                              <Typography
                                variant="body2"
                                fontWeight={600}
                                noWrap
                              >
                                {comment.name}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                noWrap
                                sx={{ display: 'block', maxWidth: 100 }}
                              >
                                {comment.email}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>

                        {/* Comment content */}
                        <TableCell sx={{ maxWidth: 220 }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: '-webkit-box',
                              WebkitLineClamp: 3,
                              WebkitBoxOrient: 'vertical',
                              overflow: 'hidden',
                              lineHeight: 1.5,
                            }}
                          >
                            {comment.content}
                          </Typography>
                        </TableCell>

                        {/* Date */}
                        <TableCell
                          sx={{
                            display: { xs: 'none', sm: 'table-cell' },
                            minWidth: 140,
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            {formatCommentDate(comment.createdAt)}
                          </Typography>
                        </TableCell>

                        {/* Delete action — only shown when user has blog.comments.manage */}
                        {canManageComments && (
                          <TableCell align="right">
                            <Tooltip title="Delete Comment">
                              <span>
                                <IconButton
                                  size="small"
                                  color="error"
                                  onClick={() => handleDeleteClick(comment)}
                                  disabled={actionLoading === comment.id}
                                >
                                  {actionLoading === comment.id ? (
                                    <CircularProgress size={16} />
                                  ) : (
                                    <DeleteIcon fontSize="small" />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>

          {/* ── Footer ── */}
          <Divider />
          <Box
            sx={{
              px: 3,
              py: 2,
              flexShrink: 0,
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button onClick={onClose} variant="outlined" startIcon={<CloseIcon />}>
              Close
            </Button>
          </Box>
        </Box>
      </Drawer>

      {/* ── Delete Confirmation Dialog — only reachable when canManageComments ── */}
      <Dialog
        open={!!deleteTarget}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Comment</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to permanently delete this comment by{' '}
            <strong>{deleteTarget?.name}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleteLoading}
            startIcon={
              deleteLoading ? (
                <CircularProgress size={16} color="inherit" />
              ) : (
                <DeleteIcon />
              )
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ToastNotification
        open={toast.open}
        message={toast.message}
        severity={toast.severity}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
      />
    </>
  );
};

export default BlogCommentManagement;


// // src/admin/pages/blogs/Blogcommentmanagement.tsx
// import React, { useState, useEffect } from 'react';
// import {
//   Avatar,
//   Box,
//   Button,
//   Chip,
//   CircularProgress,
//   Dialog,
//   DialogActions,
//   DialogContent,
//   DialogContentText,
//   DialogTitle,
//   Divider,
//   Drawer,
//   IconButton,
//   Paper,
//   Stack,
//   Table,
//   TableBody,
//   TableCell,
//   TableContainer,
//   TableHead,
//   TableRow,
//   Tooltip,
//   Typography,
// } from '@mui/material';
// import {
//   Close as CloseIcon,
//   Delete as DeleteIcon,
//   Comment as CommentIcon,
//   ChatBubbleOutline as EmptyCommentIcon,
// } from '@mui/icons-material';
// import {
//   collection,
//   getDocs,
//   doc,
//   deleteDoc,
//   orderBy,
//   query,
// } from 'firebase/firestore';
// import { db } from '../../../firebase/config';
// import { Blog } from './blog';
// import ToastNotification from '../common/Toastnotification';

// /* ===================== TYPES ===================== */

// interface Comment {
//   id: string;
//   name: string;
//   email: string;
//   content: string;
//   createdAt: any;
// }

// interface ToastState {
//   open: boolean;
//   message: string;
//   severity: 'success' | 'error' | 'warning' | 'info';
// }

// /* ===================== PROPS ===================== */

// interface BlogCommentManagementProps {
//   open: boolean;
//   blog: Blog | null;
//   onClose: () => void;
// }

// /* ===================== HELPERS ===================== */

// const toDateSafe = (timestamp: any): Date | null => {
//   if (!timestamp) return null;
//   if (typeof timestamp.toDate === 'function') return timestamp.toDate();
//   if (timestamp.seconds) return new Date(timestamp.seconds * 1000);
//   if (typeof timestamp === 'string' || typeof timestamp === 'number') {
//     const d = new Date(timestamp);
//     return isNaN(d.getTime()) ? null : d;
//   }
//   return null;
// };

// const formatCommentDate = (timestamp: any): string => {
//   const date = toDateSafe(timestamp);
//   if (!date) return '—';
//   return (
//     date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }) +
//     ' · ' +
//     date.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })
//   );
// };

// const getAvatarColor = (name: string): string => {
//   const colors = ['#717fe0', '#e07171', '#71c9e0', '#e0a871', '#71e09a', '#c471e0'];
//   let hash = 0;
//   for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
//   return colors[Math.abs(hash) % colors.length];
// };

// /* ===================== COMPONENT ===================== */

// const BlogCommentManagement: React.FC<BlogCommentManagementProps> = ({ open, blog, onClose }) => {
//   const [comments, setComments] = useState<Comment[]>([]);
//   const [loading, setLoading] = useState(false);
//   const [actionLoading, setActionLoading] = useState<string>('');

//   const [toast, setToast] = useState<ToastState>({ open: false, message: '', severity: 'info' });

//   // Delete confirmation dialog state
//   const [deleteTarget, setDeleteTarget] = useState<Comment | null>(null);
//   const [deleteLoading, setDeleteLoading] = useState(false);

//   /* ── Fetch comments whenever the drawer opens for a blog ── */
//   useEffect(() => {
//     if (open && blog?.id) {
//       fetchComments(blog.id);
//     } else {
//       setComments([]);
//     }
//   }, [open, blog?.id]);

//   const fetchComments = async (blogId: string) => {
//     try {
//       setLoading(true);
//       const commentsRef = collection(db, 'blogs', blogId, 'comments');
//       const q = query(commentsRef, orderBy('createdAt', 'asc'));
//       const snapshot = await getDocs(q);
//       const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() })) as Comment[];
//       setComments(data);
//     } catch (err) {
//       console.error('Error fetching comments:', err);
//       showToast('Failed to load comments', 'error');
//     } finally {
//       setLoading(false);
//     }
//   };

//   /* ── Toast helpers ── */
//   const showToast = (message: string, severity: ToastState['severity']) => {
//     setToast({ open: true, message, severity });
//   };

//   /* ── Delete ── */
//   const handleDeleteClick = (comment: Comment) => {
//     setDeleteTarget(comment);
//   };

//   const handleDeleteConfirm = async () => {
//     if (!deleteTarget || !blog?.id) return;
//     try {
//       setDeleteLoading(true);
//       setActionLoading(deleteTarget.id);
//       await deleteDoc(doc(db, 'blogs', blog.id, 'comments', deleteTarget.id));
//       setComments(prev => prev.filter(c => c.id !== deleteTarget.id));
//       showToast('Comment deleted successfully', 'success');
//       setDeleteTarget(null);
//     } catch (err) {
//       console.error('Error deleting comment:', err);
//       showToast('Failed to delete comment', 'error');
//     } finally {
//       setDeleteLoading(false);
//       setActionLoading('');
//     }
//   };

//   const handleDeleteCancel = () => {
//     setDeleteTarget(null);
//   };

//   /* ===================== RENDER ===================== */

//   return (
//     <>
//       <Drawer
//         anchor="right"
//         open={open}
//         onClose={onClose}
//         PaperProps={{ sx: { width: { xs: '100%', sm: 640 } } }}
//       >
//         <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>

//           {/* ── Header ── */}
//           <Box
//             sx={{
//               px: 3, py: 2,
//               borderBottom: 1, borderColor: 'divider',
//               display: 'flex', alignItems: 'center', justifyContent: 'space-between',
//               flexShrink: 0,
//               bgcolor: 'background.paper',
//             }}
//           >
//             <Stack direction="row" alignItems="center" spacing={1.5}>
//               <CommentIcon color="primary" />
//               <Box>
//                 <Typography variant="h6" sx={{ lineHeight: 1.2 }}>
//                   Comments
//                 </Typography>
//                 {blog && (
//                   <Typography
//                     variant="caption"
//                     color="text.secondary"
//                     sx={{
//                       display: 'block',
//                       maxWidth: 380,
//                       overflow: 'hidden',
//                       textOverflow: 'ellipsis',
//                       whiteSpace: 'nowrap',
//                     }}
//                   >
//                     {blog.title}
//                   </Typography>
//                 )}
//               </Box>
//             </Stack>
//             <IconButton onClick={onClose} edge="end">
//               <CloseIcon />
//             </IconButton>
//           </Box>

//           {/* ── Stats Strip ── */}
//           {!loading && comments.length > 0 && (
//             <Box
//               sx={{
//                 px: 3, py: 1.5,
//                 borderBottom: 1, borderColor: 'divider',
//                 bgcolor: 'action.hover',
//                 flexShrink: 0,
//               }}
//             >
//               <Chip
//                 label={`${comments.length} Comment${comments.length !== 1 ? 's' : ''}`}
//                 size="small"
//                 variant="outlined"
//                 sx={{ fontWeight: 600, fontSize: '0.75rem' }}
//               />
//             </Box>
//           )}

//           {/* ── Body ── */}
//           <Box
//             sx={{
//               flex: 1,
//               overflow: 'auto',
//               display: 'flex',
//               flexDirection: 'column',
//               minHeight: 0,
//               '&::-webkit-scrollbar': { width: '6px' },
//               '&::-webkit-scrollbar-track': { background: 'transparent' },
//               '&::-webkit-scrollbar-thumb': { background: 'rgba(0,0,0,0.2)', borderRadius: '3px' },
//             }}
//           >
//             {loading ? (
//               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, flexDirection: 'column', gap: 2 }}>
//                 <CircularProgress size={36} />
//                 <Typography variant="body2" color="text.secondary">Loading comments...</Typography>
//               </Box>
//             ) : comments.length === 0 ? (
//               <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, flexDirection: 'column', gap: 2, p: 4 }}>
//                 <EmptyCommentIcon sx={{ fontSize: 64, color: 'text.disabled', opacity: 0.4 }} />
//                 <Typography variant="h6" color="text.secondary">No Comments Yet</Typography>
//                 <Typography variant="body2" color="text.disabled" textAlign="center">
//                   This blog post hasn't received any comments yet.
//                 </Typography>
//               </Box>
//             ) : (
//               <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 0 }}>
//                 <Table size="medium" stickyHeader>
//                   <TableHead>
//                     <TableRow>
//                       <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Commenter</TableCell>
//                       <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Comment</TableCell>
//                       <TableCell sx={{ fontWeight: 700, bgcolor: 'background.paper', display: { xs: 'none', sm: 'table-cell' } }}>Date</TableCell>
//                       <TableCell align="right" sx={{ fontWeight: 700, bgcolor: 'background.paper' }}>Actions</TableCell>
//                     </TableRow>
//                   </TableHead>
//                   <TableBody>
//                     {comments.map(comment => (
//                       <TableRow
//                         key={comment.id}
//                         sx={{ '&:last-child td': { borderBottom: 0 } }}
//                       >
//                         {/* Commenter */}
//                         <TableCell sx={{ minWidth: 140, maxWidth: 160 }}>
//                           <Stack direction="row" spacing={1.5} alignItems="center">
//                             <Avatar
//                               sx={{
//                                 width: 36,
//                                 height: 36,
//                                 fontSize: '0.875rem',
//                                 fontWeight: 700,
//                                 bgcolor: getAvatarColor(comment.name),
//                                 flexShrink: 0,
//                               }}
//                             >
//                               {comment.name.charAt(0).toUpperCase()}
//                             </Avatar>
//                             <Box minWidth={0}>
//                               <Typography variant="body2" fontWeight={600} noWrap>
//                                 {comment.name}
//                               </Typography>
//                               <Typography
//                                 variant="caption"
//                                 color="text.secondary"
//                                 noWrap
//                                 sx={{ display: 'block', maxWidth: 100 }}
//                               >
//                                 {comment.email}
//                               </Typography>
//                             </Box>
//                           </Stack>
//                         </TableCell>

//                         {/* Comment content */}
//                         <TableCell sx={{ maxWidth: 220 }}>
//                           <Typography
//                             variant="body2"
//                             color="text.secondary"
//                             sx={{
//                               display: '-webkit-box',
//                               WebkitLineClamp: 3,
//                               WebkitBoxOrient: 'vertical',
//                               overflow: 'hidden',
//                               lineHeight: 1.5,
//                             }}
//                           >
//                             {comment.content}
//                           </Typography>
//                         </TableCell>

//                         {/* Date */}
//                         <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' }, minWidth: 140 }}>
//                           <Typography variant="caption" color="text.secondary">
//                             {formatCommentDate(comment.createdAt)}
//                           </Typography>
//                         </TableCell>

//                         {/* Actions */}
//                         <TableCell align="right">
//                           <Tooltip title="Delete Comment">
//                             <span>
//                               <IconButton
//                                 size="small"
//                                 color="error"
//                                 onClick={() => handleDeleteClick(comment)}
//                                 disabled={actionLoading === comment.id}
//                               >
//                                 {actionLoading === comment.id
//                                   ? <CircularProgress size={16} />
//                                   : <DeleteIcon fontSize="small" />}
//                               </IconButton>
//                             </span>
//                           </Tooltip>
//                         </TableCell>
//                       </TableRow>
//                     ))}
//                   </TableBody>
//                 </Table>
//               </TableContainer>
//             )}
//           </Box>

//           {/* ── Footer ── */}
//           <Divider />
//           <Box sx={{ px: 3, py: 2, flexShrink: 0, display: 'flex', justifyContent: 'flex-end' }}>
//             <Button onClick={onClose} variant="outlined" startIcon={<CloseIcon />}>
//               Close
//             </Button>
//           </Box>
//         </Box>
//       </Drawer>

//       {/* ── Delete Confirmation Dialog ── */}
//       <Dialog
//         open={!!deleteTarget}
//         onClose={handleDeleteCancel}
//         maxWidth="xs"
//         fullWidth
//       >
//         <DialogTitle>Delete Comment</DialogTitle>
//         <DialogContent>
//           <DialogContentText>
//             Are you sure you want to permanently delete this comment by{' '}
//             <strong>{deleteTarget?.name}</strong>? This action cannot be undone.
//           </DialogContentText>
//         </DialogContent>
//         <DialogActions sx={{ px: 3, pb: 2 }}>
//           <Button onClick={handleDeleteCancel} disabled={deleteLoading}>
//             Cancel
//           </Button>
//           <Button
//             variant="contained"
//             color="error"
//             onClick={handleDeleteConfirm}
//             disabled={deleteLoading}
//             startIcon={deleteLoading ? <CircularProgress size={16} /> : <DeleteIcon />}
//           >
//             Delete
//           </Button>
//         </DialogActions>
//       </Dialog>

//       <ToastNotification
//         open={toast.open}
//         message={toast.message}
//         severity={toast.severity}
//         onClose={() => setToast(t => ({ ...t, open: false }))}
//       />
//     </>
//   );
// };

// export default BlogCommentManagement;